import { Component, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { ChartConfigService } from '@app/_services';
import { PerformanceService } from '@app/performance/performance.service';
import { environment } from '@environments/environment';
import * as moment from 'moment';
import { ToastrService } from 'ngx-toastr';
import { interval, Subscription } from "rxjs";
import { Role } from "@app/_models/role";
import { NewPerformanceResultSummaryComponent } from '../new-performance-result-summary/new-performance-result-summary.component';


@Component({
  selector: 'app-new-performance-result-page',
  templateUrl: './new-performance-result-page.component.html',
  styleUrls: ['./new-performance-result-page.component.less'],
  providers: [ChartConfigService]
})
export class NewPerformanceResultPageComponent implements OnInit {
  readonly ADMIN = Role[0].role;
  readonly SUPER_ADMIN = Role[4].role;
  @ViewChild(NewPerformanceResultSummaryComponent, { static: false }) newPerformanceResultSummaryComponentChild: NewPerformanceResultSummaryComponent;

  readonly INTERVAL_TIME = 15;
  showNewGraphData = false;
  executedTaskId: any;
  perResults: any;
  queueRes: any[];
  autoRefreshInterval = this.INTERVAL_TIME;
  isAuthorized: boolean;
  testCompleted: boolean = false;
  isAutoRefresh: boolean = true;
  intervalTimer: any;
  currentPSTTime: string;
  created_date: any;
  diff: any;
  authorizationMsg: any;
  abortTriggered: boolean;
  activeTabId = 0;
  machineStats = [];
  testcaseTopLevelDetails: any;
  showLoadingDiv = false;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    private perfService: PerformanceService,
    private router: Router,
    private toastr: ToastrService,
    private chart: ChartConfigService) { }

  ngOnInit() {
    this.route.queryParams.subscribe(() => {
      this.executedTaskId = this.route.snapshot.params.id;
    });
    this.perResults = {
      testcaseExecSumm: null,
    };
    if (this.route.snapshot.routeConfig.path === "new-result/:id/queue") {
      this.getTestcaseTopDetails(this.executedTaskId);
      this.queueRes = [];
    } else if (this.route.snapshot.routeConfig.path === "new-result/:id") {
      this.resultTestCase();
    }
  }
  resultTestCase() {
    if (this.executedTaskId === "") {
      this.toastr.info("Please execute a test or select from queue");
      this.router.navigateByUrl("/performance/loadTesting");
      return false;
    }
    this.getTestcaseTopDetails(this.executedTaskId);
  }

  changeTab(event: any) {
    this.activeTabId = event.nextId;
  }

  getTestcaseTopDetails(executedTaskId?: string) {
    const id = executedTaskId || this.executedTaskId;
    const timerInterval = interval(this.autoRefreshInterval * 1000);
    this.perfService.getTestCaseTopDetails(id).subscribe((data) => {
      if (data.code === 200) {
        if(data.response.performanceRequest.usingJmxFile){
          this.showNewGraphData = true;
        }
        this.testcaseTopLevelDetails = data;
        const respData = data.response;
        this.isAuthorized = true;
        this.testCompleted = respData.executionCompleted;
        this.perResults.testcaseExecSumm = respData;

        this.perResults.testcaseExecSumm.hitsPerSec = respData.noOfHits / respData.elapsedTime || 0;

        if (this.perResults.testcaseExecSumm.name) {
          this.titleService.setTitle("Perf-Result | " + this.perResults.testcaseExecSumm.name);
        }
        // this.drawCharts(id);

        if (!respData.executionCompleted && this.isAutoRefresh) {
          if (this.intervalTimer) {
            this.intervalTimer.unsubscribe();
          }
          this.intervalTimer = timerInterval.subscribe(() => {
            this.getTestcaseTopDetails(id);
          });
        } else {
          if (this.intervalTimer) {
            this.intervalTimer.unsubscribe();
          }
        }
        this.fetchMachinesFroExecution(respData.performanceRequest.executionId);
        const todayDate = moment();
        this.currentPSTTime = todayDate.tz('America/Los_Angeles').format('YYYY-MM-DD HH:mm:ss');
        this.created_date = this.perResults.testcaseExecSumm.performanceRequest.startTime;
        this.diff = this.getDataDiff(new Date(this.created_date), new Date(this.currentPSTTime))
      } else if (data.code == 204) {
        this.isAuthorized = false;
        this.authorizationMsg = data.message;
      }
    });
  }

  fetchMachinesFroExecution(executionId) {
    this.perfService.getMachineStats(executionId).subscribe((data) => {
      this.machineStats = data.response;
    });
  }
  getDataDiff(startDate, endDate) {
    const diff = endDate.getTime() - startDate.getTime();
    const days = Math.floor(diff / (60 * 60 * 24 * 1000));
    const hours = Math.floor(diff / (60 * 60 * 1000)) - (days * 24);
    const minutes = Math.floor(diff / (60 * 1000)) - ((days * 24 * 60) + (hours * 60));
    const seconds = Math.floor(diff / 1000) - ((days * 24 * 60 * 60) + (hours * 60 * 60) + (minutes * 60));
    return { day: days, hour: hours, minute: minutes, second: seconds };
  }

  abortRunningTest() {
    this.perfService.abortResults(this.executedTaskId).subscribe((data) => {
      if (data.code === 200) {
        this.abortTriggered = true;
        this.toastr.success(data.message);
        this.router.navigate(["."], { relativeTo: this.route });
      } else {
        this.abortTriggered = false;
        this.testCompleted = true;
        this.toastr.error(data.message);
      }
    });
  }
  setAutoRefresh() {
    const stopwatch = interval(this.autoRefreshInterval * 1000);
    if (this.intervalTimer) {
      this.intervalTimer.unsubscribe();
    }
    if (this.isAutoRefresh) {
      this.intervalTimer = stopwatch.subscribe(() => {
        this.getTestcaseTopDetails();
      });
    }
  }
  autoRefreshMsg() {
    if (!this.isAutoRefresh) {
      return "Auto refresh is paused";
    }

    const hitCount = this.perResults.testcaseExecSumm?.noOfHits;
    if (hitCount !== 0) {
      return 'Please wait test is running ..';
    } else if (this.diff.minute >= 2 && hitCount === 0) {
      return 'Applying the Provided Setting';
    } else if (this.diff.minute <= 1 && hitCount === 0) {
      return 'Uploading your Testcases';
    }
  }
  formalRunFileDownload(id: string, perfId: string) {
    window.open(`${environment.apiUrl}result/performance/download/s3/${id}?request=${perfId}`);
  }

  showNewGraph(event: any) {
    this.showNewGraphData = event;
    this.showLoadingDiv = true;
    this.newPerformanceResultSummaryComponentChild.clearAllFilters();
    this.newPerformanceResultSummaryComponentChild.showNewGraph(event);
  }

  showLoadingDivEmitter(event: any) {
    this.showLoadingDiv = event;
  }
}

